import React, { useEffect, useRef, useState } from "react";
import GlobalHeader from "components/v2/global-header/GlobalHeader";
import SharedNavigation, { NavigationIds } from "components/v2/shared-navigation/SharedNavigation";
import { Spinner } from "components/v2/spinner/spinner";
import useDesktopChecker from "hooks/useDesktopChecker";
import Tasks from "pages/dashboard/home/v2/tasks/Tasks";
import Documents from "pages/dashboard/v2/documents/Documents";
import Home from "pages/dashboard/v2/home/Home";
import LoanDetails from "pages/dashboard/home/v2/loan-details/LoanDetails";
import ProfileSettings from "pages/dashboard/v2/shared/profile-settings/ProfileSettings";
import { useDispatch, useSelector } from "react-redux";
import { getActiveTab, getCurrentUserId, getNhDealApplicationId, getProfileSettings, getTaskCount, setProfileActiveTab, setTaskCount, TaskFilterType } from "features/authSlice";
import "./shared-layout.scss";
import GlobalOuterLayout from "../global-outer-layout/GlobalOuterLayout";
import { PalNotificationHandler } from "components/v2/notifications";
import { NotificationProvider, useNotificationContext } from "contexts/NotificationContext";
import useTabletChecker from "hooks/useTabletChecker";
import Footer from "components/footer/Footer";
import { PendingItem, useGetNeedsQuery } from "pages/dashboard/home/dashboardService";
import LoanDetailsMenu from "pages/dashboard/home/v2/loan-details-menu/LoanDetailsMenu";
import { useGetFeatureFlagsQuery } from "services/common/featureFlagService";
import { getTaskStatusForCount } from "utilities/needsHelper";

interface SharedLayoutProps {
  readonly children?: React.ReactNode;
  propsActiveTab?: string;
}

const SharedLayoutContent: React.FC<SharedLayoutProps> = ({ propsActiveTab }: SharedLayoutProps) => {
  const isDesktop = useDesktopChecker();
  const isTablet = useTabletChecker();
  const dispatch = useDispatch();

  const reduxActiveTab = useSelector(getActiveTab);
  const showProfile = useSelector(getProfileSettings);

  // Mobile nav toggle state
  const [isNavOpen, setIsNavOpen] = useState(false);

  const hasBootstrapped = useRef(false);
  useEffect(() => {
    if (!hasBootstrapped.current && propsActiveTab && propsActiveTab !== reduxActiveTab) {
      dispatch(setProfileActiveTab(propsActiveTab));
    }
    hasBootstrapped.current = true;
  }, [dispatch, propsActiveTab, reduxActiveTab]);

  const [showSpinner, setShowSpinner] = useState(false);
  const [spinnerDelay, setSpinnerDelay] = useState<NodeJS.Timeout | null>(null);
  const [showNotifications, setShowNotifications] = useState(false);
  const [pendingDocumentId, setPendingDocumentId] = useState<string | null>(null);
  const [isTaskCountSet, setIsTaskCountSet] = useState(false);

  const { notifications, removeNotification } = useNotificationContext();
  const userId = useSelector(getCurrentUserId);
  const nhDealApplicationId = useSelector(getNhDealApplicationId);
  const [needsList, setNeedsList] = useState<PendingItem[]>([]);
  const sidebarTaskCount = useSelector(getTaskCount);
  const needsQueryResult = useGetNeedsQuery(
    { nhDealApplicationId, userId },
    {
      refetchOnMountOrArgChange: false,
      selectFromResult: ({ data, isLoading, isFetching, error }) => ({
        data,
        isLoading,
        isFetching,
        error,
      }),
    }
  );
  
  const {
    data: pendingItemsV2,
    isLoading: isNeedsListLoading,
    isFetching: isNeedsListFetching,
    error: needsListError,
  } = needsQueryResult;
  
  const refetchNeedsList = needsQueryResult.refetch;

  // Hardcoded loan details loading flag (will be replaced with actual API call later)
  const isLoanDetailsLoading = false;
  const loanDetailsLoadError = false;

  const hideSpinner = () => {
    clearTimeout(spinnerDelay as NodeJS.Timeout);
    setShowSpinner(false);
  };

  const triggerSpinner = () => {
    const timeout = setTimeout(() => {
      setShowSpinner(true);
    }, 1000); // Delay of 1s before showing spinner
    setSpinnerDelay(timeout);
  };

  const handleTabChange = (tab: string) => {
    if (tab !== NavigationIds.More) {
      triggerSpinner();
    }
    dispatch(setProfileActiveTab(tab));
  };

  const handleMobileNotificationClick = async (notificationId: string) => {
    await removeNotification(notificationId);
    setShowNotifications(false);
  };

  const handleDocumentNotificationClick = (palInstanceId: string) => {
    if (palInstanceId) {
      setPendingDocumentId(palInstanceId);
      dispatch(setProfileActiveTab(NavigationIds.Docs));
    }
  };

  useEffect(() => {
    if (!isNeedsListLoading && !needsListError && pendingItemsV2) {
      setNeedsList(pendingItemsV2);

      const matchingNeeds = pendingItemsV2.filter(item => getTaskStatusForCount(item) === TaskFilterType.ToDo).length;
      if (!isTaskCountSet || sidebarTaskCount !== matchingNeeds) {
        dispatch(setTaskCount(matchingNeeds));
        setIsTaskCountSet(true);
      }
    }
  }, [pendingItemsV2, isNeedsListLoading, needsListError]);
  const { data: featureFlag } = useGetFeatureFlagsQuery();

  const renderContent = () => {
    if (showProfile) {
      return <ProfileSettings />;
    }
    
    switch (reduxActiveTab) {
      case NavigationIds.Tasks:
        return (
          <Tasks
            onLoad={hideSpinner}
            isNeedsListLoading={isNeedsListLoading || isNeedsListFetching}
            isNeedsListFetching={isNeedsListFetching}
            needsList={needsList}
            refetchNeedsList={refetchNeedsList} />
        );
      case NavigationIds.Docs:
        return <Documents onLoad={hideSpinner} pendingDocumentId={pendingDocumentId} onDocumentOpened={() => setPendingDocumentId(null)} />;
      case NavigationIds.LoanDetails:
        return (featureFlag?.loanDetailsEnabled === true && <LoanDetails onLoad={hideSpinner} isLoanDetailsLoading={isLoanDetailsLoading} loanDetailsLoadError={loanDetailsLoadError} /> );
      case NavigationIds.More:
        return (featureFlag?.loanDetailsEnabled === true && <LoanDetailsMenu onLoad={hideSpinner} />);
        case NavigationIds.Home:
      default:
        return <Home onLoad={hideSpinner} />;
    }
  };
  const hideFooter = reduxActiveTab === NavigationIds.More;
  const hideNotifications = reduxActiveTab === NavigationIds.More;

  const renderNavigation = () => <SharedNavigation activeTab={reduxActiveTab} onTabChange={handleTabChange} isDesktop={isDesktop} isTablet={isTablet} isNavOpen={isNavOpen} setIsNavOpen={setIsNavOpen} />;

  return (
    <GlobalOuterLayout>
      <div className="shared-spinner">{showSpinner && <Spinner isApi />}</div>
      {isDesktop && (
        <>
          {renderNavigation()}
          <div className="global-outer-layout__wrapper">
            <GlobalHeader onDocumentNotificationClick={handleDocumentNotificationClick} hideNotifications={hideNotifications}  />
            <main className="main-content">
              {renderContent()}
              <Footer />
            </main>
          </div>
        </>
      )}
      {!isDesktop && (
        <div className="mobile-container">
          {!showProfile && (
            <GlobalHeader
              onNotificationsClick={() => setShowNotifications(!showNotifications)}
              onDocumentNotificationClick={handleDocumentNotificationClick} hideNotifications={hideNotifications}
              isNavOpen={isNavOpen}
              setIsNavOpen={setIsNavOpen}
            />
          )}
          <div className="mobile-content">
            {showNotifications ? (
              <PalNotificationHandler
                notifications={notifications}
                onNotificationClick={handleMobileNotificationClick}
                onDocumentNotificationClick={handleDocumentNotificationClick}
                isOpen={showNotifications}
                onClose={() => setShowNotifications(false)}
                triggerElementSelector=".global-header__notification-button"
              />
            ) : (
              <>
                {renderContent()}
                 {!hideFooter && <Footer />}
              </>
            )}
          </div>
          {renderNavigation()}
        </div>
      )}
    </GlobalOuterLayout>
  );
};

export const SharedLayout: React.FC<SharedLayoutProps> = (props: SharedLayoutProps) => {
  return (
    <NotificationProvider>
      <SharedLayoutContent {...props} />
    </NotificationProvider>
  );
};

export default SharedLayout;
